import { AutoComplete, Breadcrumb, PopUpModal, SuccessPopupModal, TextInput, Title } from '../../Components/Atoms';
import { saveVehicleDetails, getAllModels, getBatteriesData, updateVehicleDetail, deleteVehicleFromInventory, getMakesByVehType, getModelsByVehTypeAndMake, updateVehicleInfo } from '../../APIs';
import { Button, useToast } from '@chakra-ui/react';
import { useEffect, useMemo, useState, Fragment, useContext, forwardRef, useImperativeHandle } from 'react';
import PATH from '../../Routes/paths';
import './style.css';
import { CHARGING_TYP } from '../../Library/constants';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { StoreContext } from '../../Library/contexts';
import { SuccessIcon } from '../../Assets/images/svgs';

const FIELD_TYPE = {
  TEXT: 1,
  AUTOCOMPLETE: 2,
};

const initialState = {
  reg: "",
  chNum: "",
  clrId: "",
  clrNm: "",
  mo: "",
  md: "",
  bmid: "",
  mtrNum: "",
  cTyp: "",
  imei: "",
  bId: "",
  mk: "",
  typ: "",
  ftyp: ""
};

const VEHICLE_TYPE_OPTIONS = [
  { value: 1, label: "Two wheeler" },
  { value: 2, label: "Four wheeler" },
  { value: 3, label: "Auto rickshaw" },
  { value: 4, label: "Heavy vehicle" },
];

const FUEL_TYPE_OPTIONS = [
  { value: 1, label: "Petrol" },
  { value: 2, label: "Diesel" },
  { value: 3, label: "Electric" },
  { value: 4, label: "CNG" },
];

const AddVehicle = forwardRef(({ mapDev, onSubmit }, ref) => {
  const [formData, setFormData] = useState(initialState);
  const [vehModelsOption, setVehModelsOption] = useState([]);
  const [batteriesOption, setBatteriesOption] = useState([]);
  const [vehColorOption, setVehColorOption] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [successModal, setSuccessModal] = useState({ isOpen: false, msg: "" });
  const [confirmDeleteVehicle, setConfirmDeleteVehicle] = useState({});
  const [searchParams] = useSearchParams();
  const Navigate = useNavigate();
  const location = useLocation();
  const Toast = useToast();

  const vId = searchParams.get('vid');
  const isEdit = location.state && location.state.vehicleDetail;
  const isLTMode = searchParams.get('lt') === 'true';
  const title = isEdit ? "Update Vehicle " : "Vehicle Details";

  const [vehicleMakeOptions, setVehicleMakeOptions] = useState({});
  const [vehicleModelOptions, setVehicleModelOptions] = useState({});
  const [vehicleTypeOptions, setVehicleTypeOptions] = useState(VEHICLE_TYPE_OPTIONS);
  const [vehicleFuelTypeOptions, setVehicleFuelTypeOptions] = useState(FUEL_TYPE_OPTIONS);

  const [vehicleDetails, setVehicleDetails] = useState(location.state?.vehicleDetail);

  const { userVehicles, setUserVehicles } = useContext(StoreContext);

  useImperativeHandle(ref, () => ({
    triggerSubmit: () => submitHandler()
  }));

  useEffect(() => {
    if (!vehicleDetails && userVehicles && userVehicles.length && isLTMode) {
      const vehicleId = +searchParams.get("idx");
      if (vehicleId) {
        const found = userVehicles.find(veh => veh.idx === vehicleId);
        if (found) setVehicleDetails(found);
      }
    }
  }, [userVehicles, isLTMode, vehicleDetails, searchParams]);

  useEffect(() => {
    getBatteriesData()
      .then(data => {
        const options = data.map(bat => {
          return { label: bat.bId, value: bat.bId }
        })
        setBatteriesOption(prev => [...prev, ...options])
      })
      .catch(error => console.error(error))

    getAllModels()
      .then(data => {
        const options = data.map(mo => {
          return { label: mo.nm, value: mo.idx, colors: mo.colors, bmid: mo.bmid }
        })
        setVehModelsOption(options);
      })
      .catch(error => console.error(error))

    setVehicleTypeOptions(VEHICLE_TYPE_OPTIONS);
    setVehicleFuelTypeOptions(FUEL_TYPE_OPTIONS);
  }, []);

  const fetchMakesForType = (typeId) => {
    if (!typeId) return;
    if (vehicleMakeOptions[typeId]) return;

    getMakesByVehType(typeId, (err, data) => {
      if (err) {
        console.error("getMakesByVehType error:", err);
        return;
      }
      setVehicleMakeOptions(prev => ({
        ...prev,
        [typeId]: data.map(mk => ({ value: mk.idx, label: mk.nm }))
      }));
    });
  };

  const fetchModelsForMake = (typeId, makeId) => {
    if (!makeId) return;
    if (vehicleModelOptions[makeId]) return;

    getModelsByVehTypeAndMake(typeId, makeId, (err, data) => {
      if (err) {
        console.error("getModelsByVehTypeAndMake error:", err);
        return;
      }
      setVehicleModelOptions(prev => ({
        ...prev,
        [makeId]: data.map(mo => ({ value: mo.idx, label: mo.nm }))
      }));
    });
  };

  const resolveOptionLabel = (options, idx) => {
    if (!idx) return "";
    if (!options) return "";

    if (Array.isArray(options)) {
      const found = options.find(opt => opt.value === idx || opt.idx === idx);
      return found ? found.value : "";
    }

    if (typeof options === "object") {
      for (const key of Object.keys(options)) {
        const arr = options[key];
        if (!Array.isArray(arr)) continue;
        const found = arr.find(opt => opt.value === idx || opt.idx === idx);
        if (found) return found.label;
      }
    }

    return "";
  };

  useEffect(() => {
    if (isLTMode && vehicleDetails) {
      if (vehicleDetails.typ) fetchMakesForType(vehicleDetails.typ);
      if (vehicleDetails.mk) fetchModelsForMake(vehicleDetails.typ, vehicleDetails.mk);

      const newObj = {
        reg: vehicleDetails.reg || "",
        chNum: vehicleDetails.chNum || "",
        mk: vehicleDetails.mk || "",
        md: vehicleDetails.md || "",
        typ: vehicleDetails.typ || "",
        ftyp: vehicleDetails.ftyp || ""
      };

      setFormData(prev => ({ ...prev, ...newObj }));
    }

    if (vehicleDetails && !isLTMode) {
      const newObj = {};
      Object.keys(initialState).forEach(field => newObj[field] = vehicleDetails[field]);

      const model = vehModelsOption.filter(mo => mo.value === (Number(newObj.md) ? +newObj.md : newObj.md))[0];
      if (model){
        const { colors } = model;
        if (colors && colors.length) {
          const ColorOptions = colors.map(color => ({ label: color.nm, value: color.idx }))
          setVehColorOption(ColorOptions);
        }
      }
      const batteryExist = batteriesOption.find(bd => bd.value === newObj.bId)
      if (!batteryExist)
        setBatteriesOption(prev => [{ label: vehicleDetails.bId, value: vehicleDetails.bId }, ...prev]);

      setFormData(newObj);
    }
  }, [vehicleDetails, isLTMode, vehModelsOption, batteriesOption]);

  const ltVehicleFormFields = useMemo(() => [
    {
      type: FIELD_TYPE.TEXT,
      label: "Registration Number",
      name: "reg",
      placeholder: "Enter Registration Number",
      required: true
    },
    {
      type: FIELD_TYPE.TEXT,
      label: "Chassis Number",
      name: "chNum",
      placeholder: "Enter Chassis Number",
      required: false
    },
    {
      type: FIELD_TYPE.AUTOCOMPLETE,
      label: "Vehicle Type",
      name: "typ",
      placeholder: "Select Vehicle Type",
      options: vehicleTypeOptions,
      required: true
    },
    {
      type: FIELD_TYPE.AUTOCOMPLETE,
      label: "Vehicle Make",
      name: "mk",
      placeholder: "Select Vehicle Make",
      options: vehicleMakeOptions,
      required: true
    },
    {
      type: FIELD_TYPE.AUTOCOMPLETE,
      label: "Vehicle Model",
      name: "md",
      placeholder: "Select Vehicle Model",
      options: vehicleModelOptions,
      required: true
    },
    {
      type: FIELD_TYPE.AUTOCOMPLETE,
      label: "Vehicle Fuel Type",
      name: "ftyp",
      placeholder: "Select Vehicle Fuel Type",
      options: vehicleFuelTypeOptions,
      required: true
    }
  ], [vehicleTypeOptions, vehicleMakeOptions, vehicleModelOptions, vehicleFuelTypeOptions]);

  const addVehicleFormFields = useMemo(() => [
    {
      type: FIELD_TYPE.TEXT,
      label: "Registration Number",
      name: "reg",
      placeholder: "Enter Registration Number ",
      required: true
    },
    {
      type: FIELD_TYPE.TEXT,
      label: "Chassis Number",
      name: "chNum",
      placeholder: "Enter Chassis Number ",
      required: true
    },
    {
      type: FIELD_TYPE.AUTOCOMPLETE,
      label: "Vehicle Model",
      name: "md",
      placeholder: "Select Vehicle Model",
      options: vehModelsOption,
      required: true,
      onChange: (value, Option) => {
        const {colors, label, bmid} = Option;

        setFormFieldData("md")(value);
        setFormFieldData("mo")(label);
        setFormFieldData("bmid")(bmid);
        setFormFieldData("clrId")("");

        if (colors && colors.length) {
          const ColorOptions = colors.map(color => ({ label: color.nm, value: color.idx }))
          setVehColorOption(ColorOptions);
        }
      }
    },
    {
      type: FIELD_TYPE.AUTOCOMPLETE,
      label: "Vehicle Color",
      name: "clrId",
      placeholder: "Select Vehicle Colour",
      options: vehColorOption,
      required: true,
      onChange: (value, Option) => {        
        setFormFieldData("clrId")(value);
        setFormFieldData("clrNm")(Option.label);
      }
    },
    {
      type: FIELD_TYPE.AUTOCOMPLETE,
      label: "Charger Type",
      name: "cTyp",
      placeholder: "Select Charger Type",
      options: Object.entries(CHARGING_TYP).map(([key, value]) => ({ label: key, value: value })),
      required: true
    },
    {
      type: FIELD_TYPE.TEXT,
      label: "Motor Number",
      name: "mtrNum",
      placeholder: "Enter Motor Number",
      required: true
    },
    {
      type: FIELD_TYPE.AUTOCOMPLETE,
      label: "Battery Number",
      name: "bId",
      placeholder: "Select Battery Number",
      options: batteriesOption,
      note: "Note : Add batteries to inventory before vehicle assignment",
      required: true
    },
    {
      type: FIELD_TYPE.TEXT,
      label: "Ajjas IMEI",
      name: "imei",
      placeholder: "Enter Ajjas IMEI",
      regex: /^\d{15}$/,
      required: true,
      disabled: isEdit ? true : false
    }
  ], [vehModelsOption, vehColorOption, batteriesOption, isEdit])

  const setFormFieldData = (key) => (value, option) => {
    setFormData(prev => ({
      ...prev,
      [key]: (isLTMode && option && (option.value !== undefined)) ? option.value : value
    }));
  };

  const saveVehicleData = (vehicleData) => {
    let postFunction = saveVehicleDetails;

    if (vehicleDetails) {
      postFunction = updateVehicleDetail;
      vehicleData = { ...vehicleData, vid: vId };
    }
    else vehicleData = { vehicles: [vehicleData] }

    postFunction(vehicleData, (failure, success) => {
      if (failure) {
        const errMsg = JSON.parse(failure).data;
        Toast({ isClosable: true, description: errMsg, position: "bottom", variant: "subtle", duration: "3000" })
      }
      else {
        setFormData(initialState);
        setSuccessModal(() => ({
          isOpen: true,
          msg: isEdit ? "Vehicle detail updated" : "Vehicle added successfully"
        }))
      };
    })
  };

  const submitHandler = () => {
    if (isLTMode) {
      let errors = {}, sendData = formData;
      const fields = ltVehicleFormFields;

      for (let field of fields) {
        if (field.required && !sendData[field.name]) {
          errors[field.name] = `Please enter ${field.label.toLowerCase()}`;
        }
      }

      if (Object.keys(errors).length) return setFormErrors(errors);
      else setFormErrors({});

      if (mapDev && typeof onSubmit === "function") {
        onSubmit(formData, vehicleMakeOptions[formData.typ], vehicleModelOptions[formData.mk]);
        return;
      }

      if (vehicleDetails) {
        const updatedData = { idx: vehicleDetails.idx };
        for (let key of Object.keys(sendData)) {
          const newVal = sendData[key];
          const oldVal = vehicleDetails[key];

          const isDifferent = (newVal !== oldVal);
          const isMeaningful = newVal !== "" && newVal !== undefined && newVal !== null;

          if (isDifferent && isMeaningful) updatedData[key] = newVal;
        }

        const hasChanges = Object.values(updatedData).filter(val => val);

        if (hasChanges.length <= 1) {
          return Toast({ title: "Nothing to Update", variant: "subtle", position: "bottom", isClosable: true, duration: 3000 });
        }

        if (updatedData.mk) {
          let options = vehicleMakeOptions[updatedData.typ];
          if (options && options.length) {
            let makeName = options.find(make => make.value === updatedData.mk);
            if (makeName) updatedData.vmk = makeName.label;
          }
        }

        if (updatedData.md) {
          let options = vehicleModelOptions[updatedData.mk];
          if (options && options.length) {
            let modelName = options.find(model => model.value === updatedData.md);
            if (modelName) updatedData.mo = modelName.label;
          }
        }

        updateVehicleInfo(updatedData, (err, updatedVeh) => {
          if (err) {
            console.log(err);
            Toast({ title: "Something went wrong please try again later", variant: "subtle", position: "bottom", isClosable: true, duration: 3000 });
          } else {
            setUserVehicles(prev =>
              prev.map(v => v.idx === vehicleDetails.idx ? updatedVeh : v)
            );

            const navigateToLt = () => Navigate(`${PATH.LIVE_TRACKING}/${PATH.VEHICLE_STATS}?idx=${searchParams.get("idx")}&lt=${searchParams.get("lt")}`);
            setConfirmDeleteVehicle({
              open: true,
              modalMsg: (
                <div className="veh-update-success-modal-body">
                  <SuccessIcon />
                  <p>Vehicle details updated successfully</p>
                </div>
              ),
              confirmBtn: "Okay",
              onConfirm: () => navigateToLt(),
              onModalClose: () => navigateToLt(),
            });
          }
        })
        return;
      }
    }

    let errors = {}, sendData = formData;

    // Checking if any of the required fields are empty, then show error and updating the data to sendData
    for (let field of addVehicleFormFields) {
      let {name, label} = field;
      if (!field.required)
        continue;

      if (!formData[name]) {
        errors[name] = `Please enter ${label.toLowerCase()}`;
      } else if (field.regex && !field.regex.test(formData[name])) {
        errors[name] = `Please enter valid ${label.toLowerCase()}`;
      }
    }

    // If any of the required input do not have data 
    if (Object.keys(errors).length) return setFormErrors(errors);
    else setFormErrors({});

    if (isEdit) {
      let updatedData = {};

      for (let field of Object.keys(sendData)) {
        if ((vehicleDetails[field] !== sendData[field]) && sendData[field])
          updatedData[field] = sendData[field];
      }

      if (updatedData['bId'] && vehicleDetails['bId'] && updatedData['bId'] !== vehicleDetails['bId'])
        updatedData['oldBid'] = vehicleDetails['bId'];

      // During update detail, none of the input updated
      if (!Object.keys(updatedData).length) return Toast({ title: "Nothing to Update", variant: "subtle", position: "bottom", isClosable: true });

      sendData = updatedData;
    }

    saveVehicleData(sendData);
  };

  const DeleteVehicle = (vId) => {
    deleteVehicleFromInventory(+vId, (err) => {
      if (err) {
        console.error("AddVehicle : Error while removing vehicle", err);
        setConfirmDeleteVehicle({});
        Toast({ isClosable: true, description: "Failed to remove vehicle", position: "bottom", status: "error", variant: "solid" });
      }
      else {
        setSuccessModal({ isOpen: true, msg: "Vehicle removed successfully" });
      }
    })
  }

  const deleteVehicle = () => {
    setConfirmDeleteVehicle({
      open: true,
      modalMsg: "Removing vehicle will also unmap the Ajjas device. Are you sure you want to remove vehicle from inventory?",
      confirmBtn: "Remove",
      closeBtn: "Cancel",
      onConfirm: () => DeleteVehicle(vId),
      onModalClose: () => setConfirmDeleteVehicle({}),
    });
  }

  const ltBreadcrumbPaths = [
    { idx: 1, name: "Live Tracking", link: PATH.LIVE_TRACKING },
    { idx: 2, name: "Vehicle Stats", link: `${PATH.LIVE_TRACKING}/${PATH.VEHICLE_STATS}?idx=${searchParams.get("idx")}&lt=${searchParams.get("lt")}` },
    { idx: 3, name: "Update Vehicle Details", link: "", isCurrentPage: true },
  ];

  const breadcrumbPaths = [
    { idx: 1, name: "Inventory Management", link: PATH.INVENTORY },
    { idx: 2, name: "Vehicles", link: PATH.VEHICLES },
    { idx: 3, name: title, link: "", isCurrentPage: true },
  ];

  const getOptionsForField = (fieldName) => {
    if (fieldName === 'mk') return vehicleMakeOptions[formData.typ] || [];
    if (fieldName === 'md') return vehicleModelOptions[formData.mk] || [];

    return null;
  };

  return (
    <section className={`${!mapDev ? "panel-main" : ""} add-vehicle-main`}>
      <PopUpModal {...confirmDeleteVehicle} />
      <SuccessPopupModal open={successModal.isOpen} modalMsg={successModal.msg} closeBtn={"Okay"} modalSize={"lg"} onModalClose={() => { setSuccessModal(prev => ({ ...prev, isOpen: false})); Navigate(PATH.VEHICLES) }} />

      {!mapDev ? <div>
        <Breadcrumb paths={isLTMode ? ltBreadcrumbPaths : breadcrumbPaths} />
      </div> : null}
      {/* <Button
        mb="8px"
        leftIcon={<AddModelIcon />}
        className="add-vehicle-bulk-upload-btn"
        onClick={() => Navigate("")}
      >
        Bulk Upload
      </Button> */}
      {
        isEdit && !isLTMode && <Button variant={"outline"} className='vehicle-remove-btn' onClick={deleteVehicle}>Remove from Inventory </Button>
      }
      <div className='add-vehicle'>
        <div className={`add-vehicle-card ${isLTMode ? "update-veh" : ""}`}>
          {!mapDev ? <Title>{isLTMode ? "Update Vehicle Details" : title}</Title> : null}
          <form className='add-vehicle-form' onSubmit={(e) => { e.preventDefault(); submitHandler(); }}>
            {
              (isLTMode ? ltVehicleFormFields : addVehicleFormFields).map((formField, idx) => {
                return <Fragment key={idx}>
                  {
                    formField.type === FIELD_TYPE.TEXT && (
                      <TextInput
                        inputName={formField.name || ""}
                        placeholder={formField.placeholder}
                        className="add-vehicle-text-field"
                        label={formField.label}
                        labelClassName="add-vehicle-text-field-label"
                        errMessage={formErrors[formField.name] || ""}
                        onChange={setFormFieldData(formField.name)}
                        value={formData[formField.name] || ""}
                        isRequired={formField.required}
                        disabled={formField.disabled}
                      />
                    )
                  }
                  {
                    formField.type === FIELD_TYPE.AUTOCOMPLETE && (() => {
                      let optionsToPass = formField.options;
                      if (isLTMode && (formField.name === 'mk' || formField.name === 'md')) {
                        optionsToPass = getOptionsForField(formField.name);
                      }
                      const displayValue = isLTMode ? resolveOptionLabel(optionsToPass, formData[formField.name]) : (formData[formField.name] || "");
                      const onChangeHandler = (value, option) => {
                        if (isLTMode && formField.name === 'typ' && option && option.value) {
                          setFormFieldData('typ')(option.value, option);
                          fetchMakesForType(option.value);
                          setFormData(prev => ({ ...prev, mk: "", md: "" }));
                          return;
                        }

                        if (isLTMode && formField.name === 'mk' && option && option.value) {
                          setFormFieldData('mk')(option.value, option);
                          fetchModelsForMake(formData.typ || vehicleDetails?.typ, option.value);
                          setFormData(prev => ({ ...prev, md: "" }));
                          return;
                        }

                        setFormFieldData(formField.name)(value, option);
                      };

                      return (
                        <AutoComplete
                          options={optionsToPass}
                          label={formField.label}
                          labelClassName={"add-vehicle-autocomplete-field-label"}
                          autoCompleteClassName={"add-vehicle-autocomplete"}
                          className={"add-vehicle-autocomplete-field"}
                          placeholder={formField.placeholder}
                          errMessage={formErrors[formField.name] || ""}
                          inputName={formField.name}
                          onChange={formField.onChange ? (v, o) => formField.onChange(v, o) : onChangeHandler}
                          value={displayValue}
                          note={formField.note}
                          isRequired={formField.required}
                          disabled={formField.disabled}
                        />
                      );
                    })()
                  }
                </Fragment>
              })
            }
          </form>
        </div>
        {!mapDev ? <div className="add-vehicle-submit-button">
          <Button type="submit" className="add-vehicle-button" onClick={submitHandler}>
            {isLTMode ? "Update Vehicle" : isEdit ? "Save" : 'Add Vehicle'}
          </Button>
        </div> : null}
      </div>
    </section>
  )
});

export default AddVehicle;